import React, { useEffect, useRef } from 'react';

import { XMarkIcon } from '@ergeon/icons/hero/micro';

interface UnreadDividerProps {
  onMarkRead?: () => void;
}

const UnreadDivider: React.FC<UnreadDividerProps> = ({ onMarkRead }) => {
  const dividerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const element = dividerRef.current;
    if (!element || !onMarkRead) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Start 3-second timer when divider becomes visible
          timerRef.current = setTimeout(() => {
            onMarkRead();
          }, 3000);
        } else {
          // Clear timer if divider goes out of view
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [onMarkRead]);

  return (
    <div className="flex items-center gap-3 w-full" data-unread-divider ref={dividerRef}>
      <div className="flex-1 h-px bg-red-400" />
      <button
        className="flex items-center gap-1 text-red-500 text-xs font-medium hover:text-red-600 transition-colors whitespace-nowrap"
        onClick={onMarkRead}
        type="button">
        Mark as read
        <XMarkIcon className="size-3.5" />
      </button>
      <div className="flex-1 h-px bg-red-400" />
    </div>
  );
};

export default UnreadDivider;
